import { getLayout, KEYBOARD_VARIANTS, KeyboardLayout, codeToLabel, isDvorakLayout, type LabelLayout } from './lib/keyboard-layouts';
import { Synth, WaveformType, findNearestMarker } from './lib/synth';
import { KeyboardVisualizer } from './lib/keyboard-visualizer';
import { NoteHistoryVisualizer } from './lib/note-history-visualizer';
import { MidiInput, MidiDeviceInfo } from './lib/midi-input';
import { MPEService } from './lib/mpe-service';
import { midiToCoord, coordToMidiNote } from './lib/note-colors';
import { createChordGraffiti } from './lib/chord-graffiti';
import { createIcons, Info, Search, Star, Maximize, RotateCcw, RotateCw, Settings, Settings2, Upload, X } from 'lucide';
import { mountVisOverlay } from './components/mount-vis-overlay';
import { mountGridOverlay } from './components/mount-grid-overlay';
import { mountPedals } from './components/mount-pedals';
import { mountTopBar } from './components/mount-topbar';
import { mountSongBar } from './components/mount-songbar';
import { waveformMachine } from './machines/waveformMachine';
import { pedalMachine } from './machines/pedalMachines';
import { mpeMachine } from './machines/mpeMachine';
import { gameMachine } from './machines/gameMachine';
import { parseMidi } from './lib/midi-parser';
import { buildNoteGroups, computeMedianMidiNote, findOptimalTransposition, transposeSong, foldOctaves, quantizeNotes } from './lib/game-engine';
import type { QuantizationLevel } from './lib/game-engine';
import { loadCalibratedRange, saveCalibratedRange, clearCalibratedRange } from './lib/calibration';
import { searchAllAdapters, type MidiSearchResult } from './lib/midi-search';
import { createActor } from 'xstate';
import SlimSelect from 'slim-select';
import { OverlayScrollbars } from 'overlayscrollbars';

import { isWaveformType, parseNum } from './app-helpers';
import { createSelectAtSlot, getElement, getElementOrNull } from './app-dom';
export class DComposeApp {
  private synth: Synth;
  private visualizer: KeyboardVisualizer | null = null;
  private historyVisualizer: NoteHistoryVisualizer | null = null;
  private midi: MidiInput;
  private mpe: MPEService;
  private currentLayout: KeyboardLayout;

  private octaveOffset = 0;
  private transposeOffset = 0;

  private activeNotes = new Map<string, { coordX: number; coordY: number }>();
  private lastMidiBuffer: ArrayBuffer | null = null;
  private lastMidiTitle = '';
  private noteHoldCounts = new Map<string, number>();
  private keyRepeat = new Set<string>();
  private midiChannelVoice = new Map<string, string>();
  private lastNotePerChannel = new Map<string, string>();
  private midiPitchBendRange = 24;
  private expressionBend = true;
  private expressionVelocity = true;
  private expressionPressure = true;
  private expressionTimbre = true;
  private timbreReverse = false;
  private touchDeadZone = 0.15;

  private pointerDown = new Map<number, { coordX: number; coordY: number } | null>();
  private pointerWiggle = new Map<number, { xs: number[]; ys: number[]; times: number[]; lastDir: number; changes: number }>();
  private localExpression = new Map<string, { pressure: number; pitchBend: number }>();

  private maxSimultaneousKeys = 8;
  private peakKeysThisSession = 0;
  private ghostingWarningShown = false;

  private vibratoRAF: number | null = null;
  private vibratoPhase = 0;
  private arrowLeftHeld = false;
  private arrowRightHeld = false;
  private arrowVibratoInterval: ReturnType<typeof setInterval> | null = null;
  private arrowVibratoPhase = 0;
  private cachedCanvasRect: DOMRect | null = null;
  private renderScheduled = false;
  private canvas: HTMLCanvasElement;
  private historyCanvas: HTMLCanvasElement;
  private layoutSelect: HTMLSelectElement | null = null;

  private vibratoIndicator: HTMLElement | null = null;
  private sustainIndicator: HTMLElement | null = null;
  private sustainActor: ReturnType<typeof createActor<typeof pedalMachine>> | null = null;
  private vibratoActor: ReturnType<typeof createActor<typeof pedalMachine>> | null = null;
  private midiDeviceList: HTMLElement | null = null;
  private defaultZoom = 1.0;
  private gridOverlayHandle: { toggle: () => void; setVisible: (v: boolean) => void; setZoomValue: (v: number) => void } | null = null;
  private pianoKeyPx = 0;
  private detectedDpi = 96;
  private updateGraffiti: (() => void) | null = null;
  private gameActor: ReturnType<typeof createActor<typeof gameMachine>> | null = null;
  private idleTimeout: ReturnType<typeof setTimeout> | null = null;
  private isIdle = true;
  private gameElapsedInterval: ReturnType<typeof setInterval> | null = null;

  private calibrating = false;
  private calibratedCells = new Set<string>();
  private calibratedRange: ReadonlySet<string> | null = null;

  private static readonly STORAGE_KEYS = {
    zoom: 'gi_zoom', skew: 'gi_skew', bfact: 'gi_bfact', tuning: 'gi_tuning',
    volume: 'gi_volume', waveform: 'gi_waveform', dref: 'gi_dref', layout: 'gi_layout',
    midiPbRange: 'gi_midi_pb_range',
    exprBend: 'gi_expr_bend', exprVelocity: 'gi_expr_velocity', exprPressure: 'gi_expr_pressure', exprTimbre: 'gi_expr_timbre',
    timbreCcMode: 'gi_timbre_cc_mode',
    maxKeys: 'gi_max_keys',
    pressureMode: 'gi_pressure_mode',
    timbreReverse: 'gi_timbre_reverse',
    dpi: 'gi_dpi',
    pressureCcSource: 'gi_pressure_cc_source',
    touchDeadZone: 'gi_touch_dead_zone',
  } as const;

  private loadSetting(key: keyof typeof DComposeApp.STORAGE_KEYS, fallback: string): string {
    try { return localStorage.getItem(DComposeApp.STORAGE_KEYS[key]) ?? fallback; }
    catch { return fallback; }
  }

  private saveSetting(key: keyof typeof DComposeApp.STORAGE_KEYS, value: string): void {
    try { localStorage.setItem(DComposeApp.STORAGE_KEYS[key], value); }
    catch { /* storage full or private mode */ }
  }

  private applyTimbreCcMode(cc: string): void {
    const numericCc = parseInt(cc, 10);
    if (!isNaN(numericCc) && numericCc >= 0 && numericCc <= 127) {
      this.mpe.updateSettings({ timbreCC: numericCc });
    }
  }
  constructor() {
    this.synth = new Synth();
    this.midi = new MidiInput();
    this.mpe = new MPEService();
    this.currentLayout = getLayout('ansi');

    this.canvas = getElement('keyboard-canvas', HTMLCanvasElement);
    this.historyCanvas = getElement('history-canvas', HTMLCanvasElement);

    const savedPbRange = parseFloat(this.loadSetting('midiPbRange', '24'));
    this.midiPitchBendRange = (Number.isFinite(savedPbRange) && savedPbRange > 0) ? savedPbRange : 24;
    this.expressionBend = this.loadSetting('exprBend', 'true') === 'true';
    this.expressionVelocity = this.loadSetting('exprVelocity', 'true') === 'true';
    this.expressionPressure = this.loadSetting('exprPressure', 'true') === 'true';
    this.expressionTimbre = this.loadSetting('exprTimbre', 'true') === 'true';
    this.timbreReverse = this.loadSetting('timbreReverse', 'false') === 'true';
    const savedDeadZone = parseFloat(this.loadSetting('touchDeadZone', '0.15'));
    this.touchDeadZone = (Number.isFinite(savedDeadZone) && savedDeadZone >= 0) ? savedDeadZone : 0.15;

    void this.init();
  }
  private bindMountedControls(): void {
    this.layoutSelect = getElementOrNull('layout-select', HTMLSelectElement);
    this.vibratoIndicator = getElementOrNull('vibrato-indicator', HTMLElement);
    this.sustainIndicator = getElementOrNull('sustain-indicator', HTMLElement);
    this.midiDeviceList = getElementOrNull('midi-device-list', HTMLElement);
  }
  private async init(): Promise<void> {
    createIcons({ icons: { Info, Search, Star, Maximize, RotateCcw, RotateCw, Settings, Settings2, Upload, X } });
    this.calibratedRange = loadCalibratedRange();
    this.setupVisualizer();
    if (this.calibratedRange) {
      this.visualizer?.setCalibratedRange(this.calibratedRange);
    }
    this.setupHistoryVisualizer();
    this.setupEventListeners();
    createIcons({ icons: { Info, Search, Star, Maximize, RotateCcw, RotateCw, Settings, Settings2, Upload, X } });
    await this.midi.init();
    await this.mpe.init();
    this.mpe.subscribe((voices) => {
      const expr = new Map<string, { pressure: number; pitchBend: number }>();
      for (const v of voices) {
        if (v.state === 'active') {
          expr.set(v.noteId, { pressure: v.pressure, pitchBend: v.pitchBend });
        }
      }
      this.visualizer?.setMPEExpression(expr);
      this.visualizer?.render();
    });
    this.setupMidiListeners();
    this.updateMidiDevicePanel(this.midi.getDevices());
    const keyboardContainer = document.getElementById('keyboard-container');
    if (keyboardContainer && this.visualizer) {
      this.updateGraffiti = createChordGraffiti({ container: keyboardContainer, visualizer: this.visualizer });
    }
    this.render();
    requestAnimationFrame(() => {
      this.updateGraffiti?.();
      this.setIdleState(true);
    });
  }
  private setupVisualizer(): void {
    const container = this.canvas.parentElement;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const width = Math.max(rect.width - 2, 300);
    const height = Math.max(rect.height - 2, 200);

    this.visualizer = new KeyboardVisualizer(this.canvas, { width, height });

    new ResizeObserver((entries) => {
      if (!this.visualizer) return;
      this.cachedCanvasRect = null;
      const entry = entries[0];
      const w = Math.max(entry.contentRect.width - 2, 300);
      const h = Math.max(entry.contentRect.height - 2, 200);
      this.visualizer.resize(w, h);
      this.updateGraffiti?.();
    }).observe(container);

    window.addEventListener('scroll', () => { this.cachedCanvasRect = null; }, { passive: true });
    window.addEventListener('orientationchange', () => { this.cachedCanvasRect = null; });
    document.addEventListener('scroll', () => { this.cachedCanvasRect = null; }, { passive: true, capture: true });
  }
  private setupHistoryVisualizer(): void {
    this.historyVisualizer = new NoteHistoryVisualizer(this.historyCanvas);
    this.historyVisualizer.start();

    const historyContainer = this.historyCanvas.parentElement;
    if (historyContainer) {
      new ResizeObserver((entries) => {
        if (!this.historyVisualizer) return;
        const entry = entries[0];
        this.historyVisualizer.resize(
          entry.contentRect.width > 0 ? entry.contentRect.width : 900,
          entry.contentRect.height > 0 ? entry.contentRect.height : 120
        );
      }).observe(historyContainer);
    }

    this.historyCanvas.addEventListener('wheel', (e: WheelEvent) => {
      e.preventDefault();
      if (!this.historyVisualizer) return;
      const rect = this.historyCanvas.getBoundingClientRect();
      const xInCanvas = e.clientX - rect.left;
      const PIANO_W = 52;
      if (xInCanvas <= PIANO_W) {
        const delta = e.deltaY > 0 ? 1 : -1;
        const current = this.historyVisualizer.getTimeWindow();
        const octaves = Math.round(current);
        const newVal = Math.min(8, Math.max(2, octaves + delta));
        const half = Math.floor(newVal * 12 / 2);
        this.historyVisualizer.setNoteRange(coordToMidiNote(0, 0) - half, coordToMidiNote(0, 0) + half);
      } else {
        const delta = e.deltaY > 0 ? 0.5 : -0.5;
        const current = this.historyVisualizer.getTimeWindow();
        const newVal = Math.min(10, Math.max(1, current + delta));
        this.historyVisualizer.setTimeWindow(newVal);
      }
    }, { passive: false });
  }
  private setupMidiListeners(): void {
    this.midi.onNoteOn((note, velocity, channel, deviceId) => {
      this.handleMidiNoteOn(note, velocity, channel, deviceId);
    });
    this.midi.onNoteOff((note, _velocity, channel, deviceId) => {
      this.handleMidiNoteOff(note, channel, deviceId);
    });
    this.midi.onStatusChange((devices) => {
      this.updateMidiDevicePanel(devices);
    });

    const getVoicesForChannel = (deviceId: string, channel: number): string[] => {
      const channelKey = `${deviceId}_${channel}`;
      const lastNote = this.lastNotePerChannel.get(channelKey);
      if (lastNote) return [lastNote];
      const prefix = `${deviceId}_${channel}_`;
      const fallback: string[] = [];
      for (const [key, audioId] of this.midiChannelVoice) {
        if (key.startsWith(prefix)) fallback.push(audioId);
      }
      return fallback;
    };

    this.midi.onPitchBend((channel, value, deviceId) => {
      for (const audioNoteId of getVoicesForChannel(deviceId, channel)) {
        if (this.expressionBend) this.synth.setPitchBend(audioNoteId, value * this.midiPitchBendRange);
        this.mpe.sendPitchBend(audioNoteId, value * this.midiPitchBendRange);
        const prev = this.localExpression.get(audioNoteId);
        this.localExpression.set(audioNoteId, { pressure: prev?.pressure ?? 0, pitchBend: value * this.midiPitchBendRange });
      }
      this.visualizer?.setMPEExpression(this.localExpression);
      this.render();
    });
    this.midi.onSlide((channel, value, deviceId) => {
      const v = this.timbreReverse ? 1 - value : value;
      for (const audioNoteId of getVoicesForChannel(deviceId, channel)) {
        if (this.expressionTimbre) this.synth.setTimbre(audioNoteId, v);
        this.mpe.sendSlide(audioNoteId, v);
      }
      this.visualizer?.render();
    });
    this.midi.onPressure((channel, value, deviceId) => {
      for (const audioNoteId of getVoicesForChannel(deviceId, channel)) {
        if (this.expressionPressure) this.synth.setPressure(audioNoteId, value);
        this.mpe.sendPressure(audioNoteId, value);
        const prev = this.localExpression.get(audioNoteId);
        this.localExpression.set(audioNoteId, { pressure: value, pitchBend: prev?.pitchBend ?? 0 });
      }
      this.visualizer?.setMPEExpression(this.localExpression);
      this.render();
    });
  }
  private handleMidiNoteOn(midiNote: number, velocity: number, channel: number, deviceId: string): void {
    const [coordX, coordY] = midiToCoord(midiNote);
    const noteKey = `midi_${deviceId}_${channel}_${midiNote}`;
    const audioNoteId = `midi_${deviceId}_${channel}_${midiNote}_${coordX}_${coordY}`;
    const channelVoiceKey = `${deviceId}_${channel}_${midiNote}`;
    const existing = this.activeNotes.get(noteKey);
    if (existing) {
      const oldAudioId = this.midiChannelVoice.get(channelVoiceKey);
      if (oldAudioId) {
        this.synth.stopNote(oldAudioId);
        this.mpe.noteOff(oldAudioId, midiNote);
        this.localExpression.delete(oldAudioId);
      }
      this.trackNoteOff(existing.coordX, existing.coordY);
    }
    if (this.calibrating) {
      this.calibratedCells.add(`${coordX}_${coordY}`);
      this.visualizer?.setCalibratedRange(new Set(this.calibratedCells));
    }
    this.activeNotes.set(noteKey, { coordX, coordY });
    this.midiChannelVoice.set(`${deviceId}_${channel}_${midiNote}`, audioNoteId);
    this.lastNotePerChannel.set(`${deviceId}_${channel}`, audioNoteId);
    this.trackNoteOn(coordX, coordY, midiNote);
    if (this.gameActor?.getSnapshot().matches('playing')) {
      this.gameActor.send({ type: 'NOTE_PRESSED', cellId: `${coordX}_${coordY}`, midiNote });
    }
    this.render();
    this.synth.tryUnlock();
    if (this.synth.isInitialized()) {
      this.synth.playNote(audioNoteId, coordX, coordY, 0, this.expressionVelocity ? velocity / 127 : 1);
      this.mpe.noteOn(audioNoteId, midiNote, velocity / 127);
    }
  }

  private handleMidiNoteOff(midiNote: number, channel: number, deviceId: string): void {
    const noteKey = `midi_${deviceId}_${channel}_${midiNote}`;
    const noteData = this.activeNotes.get(noteKey);
    if (!noteData) return;
    const { coordX, coordY } = noteData;
    const audioNoteId = `midi_${deviceId}_${channel}_${midiNote}_${coordX}_${coordY}`;
    this.synth.stopNote(audioNoteId);
    this.mpe.noteOff(audioNoteId, midiNote);
    this.localExpression.delete(audioNoteId);
    this.visualizer?.setMPEExpression(this.localExpression);
    this.activeNotes.delete(noteKey);
    this.midiChannelVoice.delete(`${deviceId}_${channel}_${midiNote}`);
    const chKey = `${deviceId}_${channel}`;
    if (this.lastNotePerChannel.get(chKey) === audioNoteId) this.lastNotePerChannel.delete(chKey);
    this.trackNoteOff(coordX, coordY);
    this.render();
  }
  private updateMidiDevicePanel(devices: MidiDeviceInfo[]): void {
    if (!this.midiDeviceList) return;
    this.midiDeviceList.textContent = '';

    if (!this.midi.isAvailable) {
      const msg = document.createElement('span');
      msg.className = 'midi-no-devices';
      msg.textContent = 'WebMIDI is not available in this browser. Use Chrome, Edge, or Opera for MIDI input. Safari, iOS, and Firefox do not support WebMIDI.';
      this.midiDeviceList.appendChild(msg);
      return;
    }
    if (devices.length === 0) {
      const msg = document.createElement('span');
      msg.className = 'midi-no-devices';
      msg.textContent = 'No MIDI devices detected — plug in a controller';
      this.midiDeviceList.appendChild(msg);
      return;
    }

    for (const device of devices) {
      const row = document.createElement('label');
      row.className = 'midi-device-row';

      const wrapper = document.createElement('span');
      wrapper.className = 'gi-checkbox';
      const cb = document.createElement('input');
      cb.type = 'checkbox';
      cb.checked = device.enabled;
      cb.addEventListener('change', () => {
        this.midi.setDeviceEnabled(device.id, cb.checked);
      });
      const check = document.createElement('span');
      check.className = 'gi-check';
      wrapper.appendChild(cb);
      wrapper.appendChild(check);

      const dot = document.createElement('span');
      dot.className = `midi-dot ${device.connected ? 'connected' : 'disconnected'}`;

      const name = document.createElement('span');
      name.className = 'midi-device-name';
      name.textContent = device.name + (device.manufacturer ? ` (${device.manufacturer})` : '');

      row.appendChild(wrapper);
      row.appendChild(dot);
      row.appendChild(name);
      this.midiDeviceList.appendChild(row);
    }
  }
  private setupEventListeners(): void {
    document.addEventListener('keydown', this.handleKeyDown.bind(this));
    document.addEventListener('keyup', this.handleKeyUp.bind(this));

    this.canvas.addEventListener('pointerdown', this.handlePointerDown.bind(this));
    this.canvas.addEventListener('pointermove', this.handlePointerMove.bind(this));
    this.canvas.addEventListener('pointerup', this.handlePointerUp.bind(this));
    this.canvas.addEventListener('pointerleave', this.handlePointerUp.bind(this));
    this.canvas.addEventListener('pointercancel', this.handlePointerUp.bind(this));
    this.canvas.addEventListener('contextmenu', (e) => { e.preventDefault(); });

    const visCogMount = document.getElementById('vis-cog-mount');
    const visOverlayMount = document.getElementById('vis-overlay-mount');
    if (visOverlayMount && this.historyVisualizer) {
      mountVisOverlay(visOverlayMount, visCogMount, this.historyVisualizer);
    }

    const topbarMountEl = document.getElementById('topbar-mount');
    if (topbarMountEl) {
      mountTopBar(
        topbarMountEl,
        () => { /* about dialog wired in main.ts via #about-btn id */ },
        () => {
          Object.keys(localStorage).filter(k => k.startsWith('gi_')).forEach(k => { localStorage.removeItem(k); });
          location.reload();
        },
      );
    }

    const gridCogMount = document.getElementById('grid-cog-mount');
    const gridOverlayMount = document.getElementById('grid-overlay-mount');
    if (gridOverlayMount) {
      this.gridOverlayHandle = mountGridOverlay(gridOverlayMount, gridCogMount, {
        onVolumeChange: (v: number) => { this.synth.setMasterVolume(v); this.saveSetting('volume', String(v)); },
        initialVolume: parseFloat(this.loadSetting('volume', '0.3')),
        onZoomChange: (v: number) => { this.visualizer?.setZoom(v); this.updateGraffiti?.(); this.saveSetting('zoom', String(v)); },
        initialZoom: parseFloat(this.loadSetting('zoom', this.defaultZoom.toString())),
        getDefaultZoom: () => this.defaultZoom,
        onSkewChange: (v: number) => { this.visualizer?.setSkewFactor(v); this.recalcDefaultZoom(); this.updateGraffiti?.(); this.saveSetting('skew', String(v)); },
        initialSkew: parseFloat(this.loadSetting('skew', '0')),
        onShearChange: (v: number) => { this.visualizer?.setBFact(v); this.recalcDefaultZoom(); this.updateGraffiti?.(); this.saveSetting('bfact', String(v)); },
        initialShear: parseFloat(this.loadSetting('bfact', '0')),
        onTuningChange: (v: number) => { this.synth.setFifth(v); this.visualizer?.setGenerator([v, 1200]); this.visualizer?.render(); this.updateGraffiti?.(); this.saveSetting('tuning', String(v)); },
        initialTuning: parseFloat(this.loadSetting('tuning', '700')),
        onDRefChange: (v: number) => { this.synth.setD4Hz(v); this.visualizer?.setD4Hz(v); this.visualizer?.render(); this.saveSetting('dref', String(v)); },
        initialDRef: parseFloat(this.loadSetting('dref', '293.66')),
        initialPbRange: this.midiPitchBendRange,
      });
    }

    const savedMax = this.loadSetting('maxKeys', '8');
    const parsedMax = parseInt(savedMax, 10);
    this.maxSimultaneousKeys = Number.isFinite(parsedMax) && parsedMax >= 1 ? parsedMax : 8;

    const songbarMountEl = document.getElementById('songbar-mount');
    if (songbarMountEl) {
      mountSongBar(songbarMountEl, {
        onSearch: () => { /* value read imperatively below via #midi-search-input */ },
        onSearchFocus: () => {
          const hint = document.querySelector<HTMLElement>('#song-bar-hint');
          if (hint) hint.classList.remove('show-inline');
        },
        onSearchBlur: () => {
          const searchInput = document.querySelector<HTMLInputElement>('#midi-search-input');
          const hint = document.querySelector<HTMLElement>('#song-bar-hint');
          if (searchInput && hint && searchInput.value.trim() === '') {
            const gameState = this.gameActor ? String(this.gameActor.getSnapshot().value) : undefined;
            if (!gameState || gameState === 'idle' || gameState === 'error') {
              hint.classList.add('show-inline');
            }
          }
        },
        onQuantizationCycle: () => { /* value read on-demand by loadMidiFromBuffer */ },
        onGameReset: () => {
          if (this.lastMidiBuffer) {
            this.gameActor?.send({ type: 'GAME_RESET' });
            this.loadMidiFromBuffer(this.lastMidiBuffer, this.lastMidiTitle);
          } else {
            this.gameActor?.send({ type: 'GAME_RESTART' });
          }
        },
        onCalibrateStart: () => { this.enterCalibrationMode(); },
        onCalibrateConfirm: () => { this.exitCalibrationMode(true); },
        onCalibrateCancel: () => { this.exitCalibrationMode(false); },
        onMaxKeysChange: (n: number) => {
          this.maxSimultaneousKeys = n;
          this.saveSetting('maxKeys', String(n));
          const gameState = this.gameActor ? String(this.gameActor.getSnapshot().value) : undefined;
          if ((gameState === 'playing' || gameState === 'complete') && this.lastMidiBuffer) {
            this.gameActor?.send({ type: 'GAME_RESET' });
            this.loadMidiFromBuffer(this.lastMidiBuffer, this.lastMidiTitle);
          }
        },
        initialMaxKeys: this.maxSimultaneousKeys,
      });
    }

    createSelectAtSlot('wave-select-slot', 'wave-select', [
      { value: 'sawtooth', text: 'SAW' },
      { value: 'sine', text: 'SIN' },
      { value: 'square', text: 'SQR' },
      { value: 'triangle', text: 'TRI' },
      { value: 'pluck', text: 'PLUCK' },
      { value: 'organ', text: 'ORGAN' },
      { value: 'brass', text: 'BRASS' },
      { value: 'pad', text: 'PAD' },
      { value: 'bell', text: 'BELL' },
      { value: 'bass', text: 'BASS' },
      { value: 'bright', text: 'BRIGHT' },
      { value: 'warm', text: 'WARM' },
      { value: 'guitar', text: 'GUITAR' },
    ], {});

    createSelectAtSlot('layout-select-slot', 'layout-select', [], {});

    createSelectAtSlot('mpe-output-select-slot', 'mpe-output-select', [
      { value: '', text: 'No MIDI outputs' },
    ], { style: 'min-width:120px;', disabled: '' });

    createSelectAtSlot('quantization-select-slot', 'quantization-level', [
      { value: 'none', text: 'None' },
      { value: '1/4', text: '1/4' },
      { value: '1/8', text: '1/8' },
      { value: '1/16', text: '1/16' },
    ], {});
    const quantSelect = getElementOrNull('quantization-level', HTMLSelectElement);
    if (quantSelect) {
      const qSS = new SlimSelect({ select: quantSelect, settings: { showSearch: false } });
      void qSS;
    }

    this.bindMountedControls();
    requestAnimationFrame(() => { createIcons({ icons: { Info, Search, Star, Maximize, RotateCcw, RotateCw, Settings, Settings2, Upload, X } }); });

    const gridOverlay = getElementOrNull('grid-overlay', HTMLDivElement);
    if (gridOverlay) {
      gridOverlay.setAttribute('data-overlayscrollbars-initialize', '');
      const overlayScrollbars = OverlayScrollbars(gridOverlay, {
        overflow: { x: 'hidden', y: 'scroll' },
        scrollbars: {
          theme: 'gi-scrollbar',
          visibility: 'visible',
          autoHide: 'never',
        },
      });
      document.getElementById('grid-settings-btn')?.addEventListener('click', () => {
        requestAnimationFrame(() => { requestAnimationFrame(() => { overlayScrollbars.update(); }); });
      });
    }
    const savedWaveform = this.loadSetting('waveform', 'sawtooth');
    const initialWaveform: WaveformType = isWaveformType(savedWaveform) ? savedWaveform : 'sawtooth';
    const waveformActor = createActor(waveformMachine, { input: { initial: initialWaveform } });
    const waveSelect = getElementOrNull('wave-select', HTMLSelectElement);
    let waveSS: SlimSelect | null = null;
    if (waveSelect) {
      waveSelect.value = initialWaveform;
      waveSS = new SlimSelect({
        select: waveSelect,
        settings: { showSearch: false },
        events: {
          afterChange: (newVal) => {
            const wf = newVal[0]?.value;
            if (wf && isWaveformType(wf)) waveformActor.send({ type: 'SELECT', waveform: wf });
            document.querySelectorAll<HTMLElement>('.ss-main').forEach(el => { el.blur(); });
          },
        },
      });
    }
    waveformActor.subscribe((snapshot) => {
      const active = snapshot.context.active;
      if (waveSS) waveSS.setSelected(active);
      this.synth.setWaveform(active);
      this.saveSetting('waveform', active);
    });
    waveformActor.start();
    const waveReset = getElementOrNull('wave-reset', HTMLButtonElement);
    waveReset?.addEventListener('click', () => { waveformActor.send({ type: 'SELECT', waveform: 'sawtooth' }); });
    if (this.layoutSelect) {
      for (const variant of KEYBOARD_VARIANTS) {
        const opt = document.createElement('option');
        opt.value = variant.id;
        opt.textContent = variant.name;
        this.layoutSelect.appendChild(opt);
      }
      const savedLayout = this.loadSetting('layout', '');
      if (savedLayout) {
        this.layoutSelect.value = savedLayout;
        this.currentLayout = getLayout(savedLayout);
        this.labelLayout = isDvorakLayout(savedLayout) ? 'dvorak' : 'qwerty';
      } else {
        this.currentLayout = getLayout('ansi');
        this.layoutSelect.value = 'ansi';
        try {
          const kb = (navigator as unknown as Record<string, unknown>)['keyboard'] as unknown as { getLayoutMap?: () => Promise<Map<string, string>> } | undefined;
          if (kb?.getLayoutMap) {
            kb.getLayoutMap().then((layoutMap) => {
              if (layoutMap.has('IntlBackslash')) {
                this.currentLayout = getLayout('iso');
                if (this.layoutSelect) this.layoutSelect.value = 'iso';
                this.visualizer?.render();
              }
            }).catch(() => { /* fallback to ANSI */ });
          }
        } catch { /* Keyboard API not available */ }
      }

      const layoutSS = new SlimSelect({
        select: this.layoutSelect,
        settings: { showSearch: false },
        events: {
          afterChange: (newVal) => {
            const val = newVal[0]?.value;
            if (val) {
              this.currentLayout = getLayout(val);
              this.labelLayout = isDvorakLayout(val) ? 'dvorak' : 'qwerty';
              this.saveSetting('layout', val);
              const qToggle = getElementOrNull('qwerty-overlay-toggle', HTMLInputElement);
              if (qToggle?.checked) {
                this.visualizer?.setQwertyLabels(this.buildKeyboardLabels());
                this.visualizer?.render();
              }
            }
            document.querySelector<HTMLElement>('.ss-main')?.blur();
          },
        },
      });
      const layoutReset = getElementOrNull('layout-reset', HTMLButtonElement);
      layoutReset?.addEventListener('click', () => {
        layoutSS.setSelected('ansi');
      });
    }
    const tuningSliderEl = getElementOrNull('tuning-slider', HTMLInputElement);
    if (tuningSliderEl) {
      tuningSliderEl.addEventListener('dblclick', () => {
        const currentValue = parseFloat(tuningSliderEl.value);
        const { marker } = findNearestMarker(currentValue);
        tuningSliderEl.value = marker.fifth.toString();
        tuningSliderEl.dispatchEvent(new Event('input', { bubbles: true }));
      });
    }
    const spacingInput = getElementOrNull('spacing-input', HTMLInputElement);
    spacingInput?.addEventListener('input', () => {
      this.visualizer?.setButtonSpacing(parseNum(spacingInput.value, 0));
    });
    const pbRangeInput = getElementOrNull('midi-pb-range-expr', HTMLInputElement);
    if (pbRangeInput) {
      pbRangeInput.value = this.midiPitchBendRange.toString();
      pbRangeInput.addEventListener('change', () => {
        const val = parseFloat(pbRangeInput.value);
        if (Number.isFinite(val) && val > 0) {
          this.midiPitchBendRange = val;
          this.saveSetting('midiPbRange', val.toString());
        } else {
          pbRangeInput.value = this.midiPitchBendRange.toString();
        }
      });
      pbRangeInput.addEventListener('blur', () => {
        const val = parseFloat(pbRangeInput.value);
        const resolved = (Number.isFinite(val) && val > 0) ? val : 24;
        pbRangeInput.value = resolved.toString();
        this.midiPitchBendRange = resolved;
        this.saveSetting('midiPbRange', resolved.toString());
      });
    }

    const wireCheckbox = (id: string, key: keyof typeof DComposeApp.STORAGE_KEYS, get: () => boolean, set: (v: boolean) => void): void => {
      const cb = getElementOrNull(id, HTMLInputElement);
      if (!cb) return;
      cb.checked = get();
      cb.addEventListener('change', () => { set(cb.checked); this.saveSetting(key, cb.checked.toString()); });
    };
    wireCheckbox('expr-bend', 'exprBend', () => this.expressionBend, (v) => { this.expressionBend = v; });
    wireCheckbox('expr-velocity', 'exprVelocity', () => this.expressionVelocity, (v) => { this.expressionVelocity = v; });
    const savedPressureMode = this.loadSetting('pressureMode', 'channel');
    this.expressionPressure = savedPressureMode !== 'off';
    const pressureModeSelect = createSelectAtSlot('pressure-mode-slot', 'pressure-mode', [
      { value: 'channel', text: 'Channel' },
      { value: 'poly', text: 'Poly AT' },
      { value: 'mpe', text: 'MPE' },
      { value: 'off', text: 'Off' },
    ], {});
    if (pressureModeSelect) {
      pressureModeSelect.value = savedPressureMode;
      const pmSS = new SlimSelect({ select: pressureModeSelect, settings: { showSearch: false }, events: {
        afterChange: (newVal) => { const mode = newVal[0]?.value ?? 'channel'; this.expressionPressure = mode !== 'off'; this.saveSetting('pressureMode', mode); },
      }});
      void pmSS;
    }

    wireCheckbox('expr-timbre', 'exprTimbre', () => this.expressionTimbre, (v) => { this.expressionTimbre = v; });
    wireCheckbox('timbre-reverse', 'timbreReverse', () => this.timbreReverse, (v) => { this.timbreReverse = v; });

    const savedTimbreMode = this.loadSetting('timbreCcMode', '74');
    this.applyTimbreCcMode(savedTimbreMode);
    const timbreCcSelect = createSelectAtSlot('timbre-cc-mode-slot', 'timbre-cc-mode', [
      { value: '74', text: 'CC74' },
      { value: '1', text: 'CC1' },
      { value: '11', text: 'CC11' },
    ], {});
    if (timbreCcSelect) {
      timbreCcSelect.value = savedTimbreMode;
      const tcSS = new SlimSelect({ select: timbreCcSelect, settings: { showSearch: false }, events: {
        afterChange: (newVal) => { const cc = newVal[0]?.value ?? '74'; this.applyTimbreCcMode(cc); this.saveSetting('timbreCcMode', cc); },
      }});
      void tcSS;
    }

    const savedPressureCc = this.loadSetting('pressureCcSource', 'aftertouch');
    this.midi.setPressureCC(savedPressureCc === 'aftertouch' ? null : parseInt(savedPressureCc, 10));
    const pressureCcSelect = createSelectAtSlot('pressure-cc-source-slot', 'pressure-cc-source', [
      { value: 'aftertouch', text: 'Aftertouch' },
      { value: '11', text: 'CC11' },
      { value: '1', text: 'CC1' },
    ], {});
    if (pressureCcSelect) {
      pressureCcSelect.value = savedPressureCc;
      const pcSS = new SlimSelect({ select: pressureCcSelect, settings: { showSearch: false }, events: {
        afterChange: (newVal) => { const mode = newVal[0]?.value ?? 'aftertouch'; this.midi.setPressureCC(mode === 'aftertouch' ? null : parseInt(mode, 10)); this.saveSetting('pressureCcSource', mode); },
      }});
      void pcSS;
    }
    const dzSlider = getElementOrNull('touch-dead-zone-slider', HTMLInputElement);
    const dzBadge = document.getElementById('touch-dead-zone-badge');
    if (dzSlider) {
      dzSlider.value = this.touchDeadZone.toString();
      if (dzBadge) dzBadge.textContent = this.touchDeadZone.toFixed(2);
      dzSlider.addEventListener('input', () => {
        const v = parseFloat(dzSlider.value);
        if (Number.isFinite(v) && v >= 0) {
          this.touchDeadZone = v;
          if (dzBadge) dzBadge.textContent = v.toFixed(2);
          this.saveSetting('touchDeadZone', v.toString());
        }
      });
      const dzReset = document.getElementById('touch-dead-zone-reset');
      if (dzReset) {
        dzReset.addEventListener('click', () => {
          this.touchDeadZone = 0.15;
          dzSlider.value = '0.15';
          if (dzBadge) dzBadge.textContent = '0.15';
          this.saveSetting('touchDeadZone', '0.15');
        });
      }
    }
    const mpeCheckbox = getElementOrNull('mpe-enabled', HTMLInputElement);
    const mpeSelect = getElementOrNull('mpe-output-select', HTMLSelectElement);
    const mpeActor = createActor(mpeMachine);

    let mpeSS: SlimSelect | null = null;
    if (mpeSelect) {
      mpeSS = new SlimSelect({
        select: mpeSelect,
        settings: { showSearch: false },
        events: {
          afterChange: (newVal) => {
            if (!mpeActor.getSnapshot().matches('enabled')) return;
            const outputs = this.mpe.getAvailableOutputs();
            const selected = outputs.find(o => o.id === newVal[0]?.value) ?? null;
            this.mpe.setOutput(selected);
            document.querySelectorAll<HTMLElement>('.ss-main').forEach(el => { el.blur(); });
          },
        },
      });
    }

    mpeActor.subscribe((snapshot) => {
      const isEnabled = snapshot.matches('enabled');
      if (mpeCheckbox) mpeCheckbox.checked = isEnabled;
      if (mpeSS) {
        if (isEnabled) { mpeSS.enable(); } else { mpeSS.disable(); }
      }
    });

    mpeActor.start();

    const refreshMpeOutputs = (): void => {
      if (!mpeSS) return;
      const outputs = this.mpe.getAvailableOutputs();
      if (outputs.length === 0) {
        mpeSS.setData([{ text: 'No MIDI outputs', value: '', placeholder: true }]);
        mpeSS.disable();
          return;
      }
      mpeSS.setData(outputs.map(o => ({ text: o.name ?? o.id, value: o.id })));
      if (!mpeActor.getSnapshot().matches('enabled')) {
        mpeSS.disable();
      }
    };

    mpeCheckbox?.addEventListener('change', () => {
      mpeActor.send({ type: 'TOGGLE' });
      const enabled = mpeActor.getSnapshot().matches('enabled');
      this.mpe.setEnabled(enabled);
      if (enabled) {
        const selectedId = mpeSelect?.value;
        const outputs = this.mpe.getAvailableOutputs();
        const selected = outputs.find(o => o.id === selectedId) ?? outputs[0];
        this.mpe.setOutput(selected);
      } else {
        this.mpe.setOutput(null);
      }
    });

    refreshMpeOutputs();
    const midiAccess = this.mpe.getMidiAccess();
    if (midiAccess) {
      midiAccess.onstatechange = () => { refreshMpeOutputs(); };
    }
    document.addEventListener('keydown', (e) => {
      if (e.code === 'Space' && e.target === document.body) e.preventDefault();
    });


     const TARGET_KEY_MM = 23.5;
     const MM_PER_CHAR_AT_12PX = 1.74;
     const charsNeeded = Math.round(TARGET_KEY_MM / MM_PER_CHAR_AT_12PX);
     const measureEl = document.createElement('span');
     measureEl.style.cssText = 'position:absolute;visibility:hidden;font-family:"JetBrains Mono",monospace;font-size:12px;white-space:pre;';
     measureEl.textContent = 'M'.repeat(charsNeeded);
     document.body.appendChild(measureEl);
     const pianoKeyPx = measureEl.offsetWidth;
     document.body.removeChild(measureEl);
     const detectPhysicalDPI = (): number => {
       if (typeof window.matchMedia !== 'function') {
         return Math.max(72, Math.min(300, 96 * window.devicePixelRatio));
       }
       let lo = 40, hi = 600;
       while (lo < hi) {
         const mid = Math.floor((lo + hi + 1) / 2);
         if (window.matchMedia(`(min-resolution: ${mid}dpi)`).matches) {
           lo = mid;
         } else {
           hi = mid - 1;
         }
       }
       return lo;
     };

     this.detectedDpi = detectPhysicalDPI();
     const savedDpiOverride = parseFloat(this.loadSetting('dpi', ''));
     const physicalDPI = (savedDpiOverride >= 40 && savedDpiOverride <= 600)
       ? savedDpiOverride
       : this.detectedDpi;
     if (physicalDPI !== this.detectedDpi) {
       this.visualizer?.setCssPxPerInch(physicalDPI);
     }
     if (!this.visualizer) throw new Error('visualizer must be initialized before zoom');
     const geometry = this.visualizer.getGridGeometry();
     const hv1 = geometry.cellHv1, hv2 = geometry.cellHv2;
     const side1 = 2 * Math.sqrt(hv1.x * hv1.x + hv1.y * hv1.y);
     const side2 = 2 * Math.sqrt(hv2.x * hv2.x + hv2.y * hv2.y);
     const gridCellSidePx = Math.min(side1, side2);

     this.pianoKeyPx = pianoKeyPx;
     const dpiBasedZoom = pianoKeyPx / gridCellSidePx;
     this.defaultZoom = dpiBasedZoom;
     const savedZoom = parseFloat(this.loadSetting('zoom', this.defaultZoom.toString()));
     const zoomToApply = (Number.isFinite(savedZoom) && savedZoom > 0.1 && savedZoom < 10) ? savedZoom : this.defaultZoom;
     this.visualizer?.setZoom(zoomToApply);
     this.gridOverlayHandle?.setZoomValue(zoomToApply);
     const dpiOverride = getElementOrNull('dpi-override', HTMLInputElement);
     const dpiOverrideReset = getElementOrNull('dpi-override-reset', HTMLButtonElement);
     const savedDpiStr = this.loadSetting('dpi', '');
     if (dpiOverride && savedDpiStr !== '') {
       dpiOverride.value = savedDpiStr;
     }
     const applyDpi = (dpi: number): void => {
       this.visualizer?.setCssPxPerInch(dpi);
       const zoomEl = document.getElementById('zoom-slider') as HTMLInputElement | null;
       if (zoomEl) {
         zoomEl.value = this.defaultZoom.toString();
         zoomEl.dispatchEvent(new Event('input', { bubbles: true }));
       }
     };
     dpiOverride?.addEventListener('change', () => {
       if (!dpiOverride.value.trim()) {
         this.saveSetting('dpi', '');
         applyDpi(this.detectedDpi);
         return;
       }
       const dpiVal = parseFloat(dpiOverride.value);
       if (dpiVal >= 40 && dpiVal <= 600) {
         this.saveSetting('dpi', dpiOverride.value);
         applyDpi(dpiVal);
       }
     });
     dpiOverrideReset?.addEventListener('click', () => {
       if (dpiOverride) dpiOverride.value = '';
       this.saveSetting('dpi', '');
       applyDpi(this.detectedDpi);
     });
    const qwertyToggle = getElementOrNull('qwerty-overlay-toggle', HTMLInputElement);
    if (qwertyToggle) {
      if (qwertyToggle.checked) {
        this.visualizer?.setQwertyLabels(this.buildKeyboardLabels());
        this.visualizer?.render();
      }
      qwertyToggle.addEventListener('change', () => {
        if (qwertyToggle.checked) {
          this.visualizer?.setQwertyLabels(this.buildKeyboardLabels());
        } else {
          this.visualizer?.setQwertyLabels(new Map());
        }
        this.visualizer?.render();
      });
    }


    window.addEventListener('blur', () => { this.stopAllNotes(); });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this.stopAllNotes();
    });
    document.querySelectorAll<HTMLElement>('select, input[type="range"], input[type="checkbox"]').forEach(el => {
      el.addEventListener('pointerup', () => requestAnimationFrame(() => { el.blur(); }));
      el.addEventListener('change', () => requestAnimationFrame(() => { el.blur(); }));
    });

    document.querySelectorAll<HTMLInputElement>('input[type="text"]').forEach(el => {
      el.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          el.blur();
        }
      });
    });

    this.sustainActor = createActor(pedalMachine);
    this.vibratoActor = createActor(pedalMachine);

    const pedalsMountEl = document.getElementById('pedals-mount');
    if (pedalsMountEl && this.sustainActor && this.vibratoActor) {
      mountPedals(pedalsMountEl, this.sustainActor, this.vibratoActor);
      this.bindMountedControls();
    }

    this.sustainActor.subscribe((snapshot) => {
      const active = snapshot.matches('active');
      this.sustainIndicator?.classList.toggle('active', active);
      this.synth.setSustain(active);
    });
    this.sustainActor.start();

    this.vibratoActor.subscribe((snapshot) => {
      const active = snapshot.matches('active');
      this.vibratoIndicator?.classList.toggle('active', active);
      this.synth.setVibrato(active);
    });
    this.vibratoActor.start();
    this.gameActor = createActor(gameMachine);
    this.gameActor.start();

    this.gameActor.subscribe((snapshot) => {
      const state = String(snapshot.value);
      const ctx = snapshot.context;

      this.visualizer?.setGameState(state);
      if (state === 'playing') {
        this.visualizer?.setGameProgress(ctx.currentGroupIndex, ctx.noteGroups.length, Date.now() - ctx.startTimeMs);
      }

      const statusEl = document.querySelector<HTMLElement>('#game-status');
      const progressFill = document.querySelector<HTMLElement>('#game-progress-fill');
      const elapsedTimer = document.querySelector<HTMLElement>('#game-elapsed-timer');
      const songBarHint = document.getElementById('song-bar-hint');

      const tuningSliderDisable = getElementOrNull('tuning-slider', HTMLInputElement);
      if (tuningSliderDisable) tuningSliderDisable.disabled = state === 'playing';

      const calibrateBtn = getElementOrNull('calibrate-btn', HTMLButtonElement);
      if (calibrateBtn) calibrateBtn.disabled = state === 'playing';

      if (state === 'playing') {
        if (this.idleTimeout !== null) {
          clearTimeout(this.idleTimeout);
          this.idleTimeout = null;
        }

        const currentGroup = ctx.noteGroups[ctx.currentGroupIndex];
        if (currentGroup && this.visualizer) {
          const mirrorCells = this.visualizer.getCellIdsForMidiNotes(new Set(currentGroup.midiNotes));
          let allTargetCellIds = mirrorCells.length > 0 ? mirrorCells : [...currentGroup.cellIds];
          const calRange = this.calibratedRange;
          if (calRange && calRange.size > 0) {
            allTargetCellIds = allTargetCellIds.filter(id => calRange.has(id));
          }
          this.visualizer.setTargetNotes(allTargetCellIds);

          if (!this.ghostingWarningShown && allTargetCellIds.length > 3 && this.peakKeysThisSession > 0 && allTargetCellIds.length > this.peakKeysThisSession) {
            this.ghostingWarningShown = true;
            const hint = document.getElementById('song-bar-hint');
            if (hint) {
              hint.classList.add('show-inline');
              hint.classList.add('visible-full');
              hint.classList.remove('faded');
              hint.textContent = `This chord needs ${allTargetCellIds.length} keys — your keyboard may limit to ${this.peakKeysThisSession}. Try a MIDI controller.`;
              setTimeout(() => { hint.classList.remove('visible-full'); hint.classList.add('faded'); }, 5000);
            }
          }

          if (ctx.pressedCellIds.length > 0) {
            this.visualizer.setPressedTargetNotes(ctx.pressedCellIds);
          } else {
            this.visualizer.setPressedTargetNotes([]);
          }
        } else {
          this.visualizer?.setTargetNotes(ctx.targetCellIds);
          this.visualizer?.setPressedTargetNotes([]);
        }
        const firstCellId = ctx.targetCellIds[0];
        if (firstCellId) {
          const parts = firstCellId.split('_');
          const x = parseInt(parts[0] ?? '0', 10);
          const y = parseInt(parts[1] ?? '0', 10);
          const midiNote = coordToMidiNote(x, y);
          this.historyVisualizer?.setGhostNote(midiNote);
        } else {
          this.historyVisualizer?.setGhostNote(null);
        }
        this.render();
        if (statusEl) statusEl.classList.add('show-flex');
        if (songBarHint) songBarHint.classList.remove('show-inline');

        if (progressFill && ctx.noteGroups.length > 0) {
          const pct = (ctx.currentGroupIndex / ctx.noteGroups.length) * 100;
          progressFill.style.width = `${pct}%`;
        }

        if (!this.gameElapsedInterval && ctx.startTimeMs > 0) {
          const startMs = ctx.startTimeMs;
          const updateTimer = (): void => {
            const el = document.getElementById('game-elapsed-timer');
            if (el) {
              const totalSec = Math.floor((Date.now() - startMs) / 1000);
              const minutes = Math.floor(totalSec / 60);
              const seconds = totalSec % 60;
              el.textContent = `${minutes}:${seconds.toString().padStart(2, '0')}`;
            }
          };
          updateTimer();
          this.gameElapsedInterval = setInterval(updateTimer, 1000);
        }
      } else if (state === 'loading') {
        if (statusEl) statusEl.classList.add('show-flex');
        if (songBarHint) songBarHint.classList.remove('show-inline');
        if (progressFill) progressFill.style.width = '0%';
        if (elapsedTimer) elapsedTimer.textContent = '';
      } else if (state === 'complete') {
        if (this.gameElapsedInterval !== null) {
          clearInterval(this.gameElapsedInterval);
          this.gameElapsedInterval = null;
        }
        this.visualizer?.setTargetNotes([]);
        this.visualizer?.setPressedTargetNotes([]);
        this.historyVisualizer?.setGhostNote(null);
        this.resetIdleTimer();
        this.updateGraffiti?.();
        this.render();
        const elapsedMs = ctx.finishTimeMs - ctx.startTimeMs;
        const elapsedSec = (elapsedMs / 1000).toFixed(1);
        this.showGameScore(elapsedSec);
        if (statusEl) statusEl.classList.add('show-flex');
        if (songBarHint) songBarHint.classList.remove('show-inline');
        if (progressFill) progressFill.style.width = '100%';
        if (elapsedTimer) {
          const totalSec = Math.floor(parseFloat(elapsedSec));
          const minutes = Math.floor(totalSec / 60);
          const seconds = totalSec % 60;
          elapsedTimer.textContent = `${minutes}:${seconds.toString().padStart(2, '0')}`;
        }
      } else {
        if (this.gameElapsedInterval !== null) {
          clearInterval(this.gameElapsedInterval);
          this.gameElapsedInterval = null;
        }
        this.visualizer?.setTargetNotes([]);
        this.visualizer?.setPressedTargetNotes([]);
        this.historyVisualizer?.setGhostNote(null);
        this.resetIdleTimer();
        this.updateGraffiti?.();
        this.render();
        if (statusEl) statusEl.classList.remove('show-flex');
        if (songBarHint) songBarHint.classList.add('show-inline');
        if (progressFill) progressFill.style.width = '0%';
        if (elapsedTimer) elapsedTimer.textContent = '';
      }
    });
    const songBar = document.getElementById('song-bar');
    document.body.addEventListener('dragover', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
      if (songBar) songBar.classList.add('dropping');
    });

    document.body.addEventListener('dragleave', (e) => {
      e.preventDefault();
      if (!e.relatedTarget || !(e.relatedTarget instanceof Node) || !(document.documentElement.contains(e.relatedTarget))) {
        if (songBar) songBar.classList.remove('dropping');
      }
    });

    document.body.addEventListener('drop', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (songBar) songBar.classList.remove('dropping');

      const files = e.dataTransfer?.files;
      if (!files || files.length === 0) return;

      const file = files[0];
      if (!file) return;
      if (!file.name.toLowerCase().endsWith('.mid') && !file.name.toLowerCase().endsWith('.midi')) {
        return;
      }

      const songTitle = file.name.replace(/\.(mid|midi)$/i, '');
      file.arrayBuffer().then((buffer) => {
        this.lastMidiBuffer = buffer;
        this.lastMidiTitle = songTitle;
        this.loadMidiFromBuffer(buffer, songTitle);
      }).catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : 'Failed to read file';
        const actor = this.gameActor;
        if (actor) actor.send({ type: 'LOAD_FAILED', error: msg });
      });
    });

    document.body.addEventListener('midi-file-upload', ((e: CustomEvent<File>) => {
      const file = e.detail;
      const songTitle = file.name.replace(/\.(mid|midi)$/i, '');
      file.arrayBuffer().then((buffer) => {
        this.lastMidiBuffer = buffer;
        this.lastMidiTitle = songTitle;
        this.loadMidiFromBuffer(buffer, songTitle);
      }).catch(() => { /* handled by game actor */ });
    }) as EventListener);
    const searchInput = getElementOrNull('midi-search-input', HTMLInputElement);
    const resultsDiv = document.getElementById('midi-search-results');
    if (searchInput && resultsDiv) {
      let searchDebounce: ReturnType<typeof setTimeout> | null = null;
      searchInput.addEventListener('input', () => {
        if (searchDebounce !== null) clearTimeout(searchDebounce);
        searchDebounce = setTimeout(() => void (async () => {
          const query = searchInput.value.trim();
          if (query.length < 2) { resultsDiv.textContent = ''; resultsDiv.classList.remove('show-flex'); return; }
          resultsDiv.classList.add('show-flex');
          resultsDiv.textContent = 'Searching\u2026';
          try {
            const { results, errors } = await searchAllAdapters(query);
            if (results.length === 0) {
              const errMsg = errors.length > 0 ? errors.join('; ') : 'no matches';
              resultsDiv.textContent = `No results — ${errMsg}`;
              return;
            }
            resultsDiv.textContent = '';
            for (const r of results) {
              const row = document.createElement('div');
              row.className = 'search-result';
              row.dataset.fetchUrl = r.fetchUrl;

              const titleSpan = document.createElement('span');
              titleSpan.className = 'result-title';
              titleSpan.textContent = r.title;

              const sourceSpan = document.createElement('span');
              sourceSpan.className = 'result-source';
              sourceSpan.textContent = r.source;

              row.appendChild(titleSpan);
              row.appendChild(sourceSpan);

              row.addEventListener('click', () => {
                void this.handleSearchResultClick(r);
                resultsDiv.classList.remove('show-flex');
                searchInput.value = '';
              });

              resultsDiv.appendChild(row);
            }
          } catch {
            resultsDiv.textContent = 'Search failed';
          }
        })(), 300);
      });
    }
  }
  private handleKeyDown(event: KeyboardEvent): void {
    const target = event.target instanceof HTMLElement ? event.target : null;
    if (!target) return;
    if (target.tagName === 'TEXTAREA') return;
    if (target.tagName === 'INPUT') {
      const t = target instanceof HTMLInputElement ? target.type : '';
      if (!t) return;
      if (t === 'text' || t === 'number') return;
    }

    const code = event.code;
    if (event.ctrlKey || event.metaKey) return;

    if (code === 'ArrowLeft' || code === 'ArrowRight') {
      event.preventDefault();
      if (code === 'ArrowLeft') this.arrowLeftHeld = true;
      if (code === 'ArrowRight') this.arrowRightHeld = true;
      if (this.arrowLeftHeld && this.arrowRightHeld) {
        this.startArrowVibrato();
      } else {
        this.stopArrowVibrato();
        const bendSemitones = code === 'ArrowLeft' ? -1 : 1;
        for (const [noteCode, noteData] of this.activeNotes) {
          const cx = noteData.coordX + this.transposeOffset;
          const cy = noteData.coordY + this.octaveOffset;
          const noteId = `key_${noteCode}_${cx}_${cy}`;
          this.synth.setPitchBend(noteId, bendSemitones);
          const prev = this.localExpression.get(noteId);
          this.localExpression.set(noteId, { pressure: prev?.pressure ?? 0, pitchBend: bendSemitones });
        }
        this.visualizer?.setMPEExpression(this.localExpression);
      }
      return;
    }

    const isSynthKey =
      code === 'ShiftLeft' || code === 'ShiftRight' ||
      code === 'Space' ||
      code in this.currentLayout.keyMap;

    if (!isSynthKey) return;

    event.preventDefault();

    if (this.keyRepeat.has(code)) return;
    this.keyRepeat.add(code);

    if (code === 'ShiftLeft' || code === 'ShiftRight') {
      this.vibratoActor?.send({ type: 'ACTIVATE' });
      this.synth.tryUnlock();
      if (!this.synth.isInitialized()) return;
      this.startMpeVibrato();
      return;
    }
    if (code === 'Space') {
      this.sustainActor?.send({ type: 'ACTIVATE' });
      this.synth.tryUnlock();
      if (!this.synth.isInitialized()) return;
      return;
    }
    this.synth.tryUnlock();                  // synchronous, iOS-safe

    if (!(code in this.currentLayout.keyMap)) return;
    const coord = this.currentLayout.keyMap[code];

    const [coordX, coordY] = coord;
    const effectiveCoordX = coordX + this.transposeOffset;
    const effectiveCoordY = coordY + this.octaveOffset;
    const audioNoteId = `key_${code}_${effectiveCoordX}_${effectiveCoordY}`;
    const midiNote = coordToMidiNote(effectiveCoordX, effectiveCoordY);
    if (this.calibrating) {
      this.calibratedCells.add(`${effectiveCoordX}_${effectiveCoordY}`);
      this.visualizer?.setCalibratedRange(new Set(this.calibratedCells));
    }
    this.activeNotes.set(code, { coordX, coordY });
    this.peakKeysThisSession = Math.max(this.peakKeysThisSession, this.activeNotes.size);
    this.trackNoteOn(effectiveCoordX, effectiveCoordY, midiNote);
    if (this.gameActor?.getSnapshot().matches('playing')) {
      this.gameActor.send({ type: 'NOTE_PRESSED', cellId: `${effectiveCoordX}_${effectiveCoordY}`, midiNote });
    }
    this.render();
    if (this.synth.isInitialized()) {
      this.synth.playNote(audioNoteId, effectiveCoordX, coordY, this.octaveOffset);
      this.mpe.noteOn(audioNoteId, midiNote, 0.7);
    }
  }
  private handleKeyUp(event: KeyboardEvent): void {
    const code = event.code;
    this.keyRepeat.delete(code);

    if (code === 'ArrowLeft' || code === 'ArrowRight') {
      if (code === 'ArrowLeft') this.arrowLeftHeld = false;
      if (code === 'ArrowRight') this.arrowRightHeld = false;
      this.stopArrowVibrato();
      if (this.arrowLeftHeld || this.arrowRightHeld) {
        const bendSemitones = this.arrowLeftHeld ? -1 : 1;
        for (const [noteCode, noteData] of this.activeNotes) {
          const cx = noteData.coordX + this.transposeOffset;
          const cy = noteData.coordY + this.octaveOffset;
          const noteId = `key_${noteCode}_${cx}_${cy}`;
          this.synth.setPitchBend(noteId, bendSemitones);
          const prev = this.localExpression.get(noteId);
          this.localExpression.set(noteId, { pressure: prev?.pressure ?? 0, pitchBend: bendSemitones });
        }
      } else {
        for (const [noteCode, noteData] of this.activeNotes) {
          const cx = noteData.coordX + this.transposeOffset;
          const cy = noteData.coordY + this.octaveOffset;
          const noteId = `key_${noteCode}_${cx}_${cy}`;
          this.synth.setPitchBend(noteId, 0);
          const prev = this.localExpression.get(noteId);
          if (prev) this.localExpression.set(noteId, { pressure: prev.pressure, pitchBend: 0 });
        }
      }
      this.visualizer?.setMPEExpression(this.localExpression);
      return;
    }

    if (code === 'ShiftLeft' || code === 'ShiftRight') {
      this.vibratoActor?.send({ type: 'DEACTIVATE' });
      this.stopMpeVibrato();
      return;
    }
    if (code === 'Space') {
      this.sustainActor?.send({ type: 'DEACTIVATE' });
      return;
    }

    const noteData = this.activeNotes.get(code);
    if (!noteData) return;
    const { coordX, coordY } = noteData;
    const effectiveCoordX = coordX + this.transposeOffset;
    const effectiveCoordY = coordY + this.octaveOffset;
    const audioNoteId = `key_${code}_${effectiveCoordX}_${effectiveCoordY}`;
    const midiNote = coordToMidiNote(effectiveCoordX, effectiveCoordY);
    this.synth.stopNote(audioNoteId);
    this.mpe.noteOff(audioNoteId, midiNote);
    this.localExpression.delete(audioNoteId);
    this.visualizer?.setMPEExpression(this.localExpression);
    this.activeNotes.delete(code);
    this.trackNoteOff(effectiveCoordX, effectiveCoordY);
    this.render();
  }
  private handlePointerDown(event: PointerEvent): void {
    this.pointerDown.set(event.pointerId, null);
    this.synth.tryUnlock();
    this.handlePointerDownInner(event);
  }

  private handlePointerDownInner(event: PointerEvent): void {
    try { this.canvas.setPointerCapture(event.pointerId); } catch { /* iOS Safari */ }
    const button = this.getButtonAtPointer(event);
    if (button) this.playPointerNote(event.pointerId, button.coordX, button.coordY, event.pressure);
    this.pointerDown.set(event.pointerId, button);
  }

  private handlePointerMove(event: PointerEvent): void {
    if (!this.pointerDown.has(event.pointerId)) return;
    const currentButton = this.pointerDown.get(event.pointerId);
    if (!currentButton) return;
    const newButton = this.getButtonAtPointer(event);
    const currentId = `${currentButton.coordX}_${currentButton.coordY}`;
    const newId = newButton ? `${newButton.coordX}_${newButton.coordY}` : null;

    if (currentId !== newId) {
      this.stopPointerNote(event.pointerId, currentButton.coordX, currentButton.coordY);
      if (newButton) this.playPointerNote(event.pointerId, newButton.coordX, newButton.coordY, event.pressure);
      this.pointerDown.set(event.pointerId, newButton);
    } else {
      const now = performance.now();
      let wg = this.pointerWiggle.get(event.pointerId);
      if (!wg) {
        wg = { xs: [event.clientX], ys: [event.clientY], times: [now], lastDir: 0, changes: 0 };
        this.pointerWiggle.set(event.pointerId, wg);
      } else {
        const prevX = wg.xs[wg.xs.length - 1];
        const dx = event.clientX - prevX;
        wg.xs.push(event.clientX);
        wg.ys.push(event.clientY);
        wg.times.push(now);
        while (wg.times.length > 1 && now - wg.times[0] > 500) {
          wg.times.shift();
          wg.xs.shift();
          wg.ys.shift();
          wg.changes = Math.max(0, wg.changes - 1);
        }
        if (Math.abs(dx) > 3) {
          const dir = dx > 0 ? 1 : -1;
          if (wg.lastDir !== 0 && dir !== wg.lastDir) wg.changes++;
          wg.lastDir = dir;
        }
      }
      const wiggling = wg.changes >= 3;
      if (wiggling && !this.synth.getVibrato()) {
        this.synth.setVibrato(true);
      } else if (!wiggling && this.synth.getVibrato() && !this.arrowLeftHeld && !this.arrowRightHeld) {
        this.synth.setVibrato(false);
      }
      const effectiveCoordX = currentButton.coordX + this.transposeOffset;
      const effectiveCoordY = currentButton.coordY + this.octaveOffset;
      const noteId = `ptr_${event.pointerId}_${effectiveCoordX}_${effectiveCoordY}`;
      if (this.visualizer) {
        const { cellHv1, cellHv2 } = this.visualizer.getGridGeometry();
        const buttons = this.visualizer.getButtons();
        const btn = buttons.find(b => b.coordX === currentButton.coordX && b.coordY === currentButton.coordY);
        if (btn) {
          const rect = this.getCanvasRect();
          const dx = (event.clientX - rect.left) - btn.x;
          const dy = (event.clientY - rect.top) - btn.y;

          const pitchDirLen = Math.sqrt(cellHv1.x * cellHv1.x + cellHv1.y * cellHv1.y);
          const pitchOffset = (dx * cellHv1.x + dy * cellHv1.y) / pitchDirLen;
          const cellWidth = pitchDirLen * 2;
          const fifthCents = this.synth.getGenerator()[0];
          const wholetoneCents = 2 * fifthCents - 1200;
          const rawSemitones = (pitchOffset / cellWidth) * wholetoneCents / 100;
          const pw = this.pointerWiggle.get(event.pointerId);
          const velocityPxPerMs = (() => {
            if (!pw || pw.xs.length < 2) return 0;
            const n = pw.xs.length;
            const lookback = Math.max(0, n - 4);
            const dt = pw.times[n - 1] - pw.times[lookback];
            if (dt < 1) return 0;
            const tdx = pw.xs[n - 1] - pw.xs[lookback];
            const tdy = pw.ys[n - 1] - pw.ys[lookback];
            return Math.sqrt(tdx * tdx + tdy * tdy) / dt;
          })();
          const isActivelySliding = velocityPxPerMs > this.touchDeadZone;
          const semitones = isActivelySliding ? rawSemitones : 0;

          const timbreDirLen = Math.sqrt(cellHv2.x * cellHv2.x + cellHv2.y * cellHv2.y);
          const timbreOffset = (dx * cellHv2.x + dy * cellHv2.y) / timbreDirLen;
          const normalizedY = Math.max(0, Math.min(1, (timbreOffset / timbreDirLen + 1) / 2));

          if (this.expressionBend) this.synth.setPitchBend(noteId, semitones);
          if (this.expressionTimbre) this.synth.setTimbre(noteId, this.timbreReverse ? 1 - normalizedY : normalizedY);
          if (this.expressionPressure && event.pressure > 0) this.synth.setPressure(noteId, event.pressure);
          const prevExpr = this.localExpression.get(noteId);
          const newPressure = (this.expressionPressure && event.pressure > 0) ? event.pressure : (prevExpr?.pressure ?? 0);
          this.localExpression.set(noteId, { pressure: newPressure, pitchBend: semitones });
          this.visualizer?.setMPEExpression(this.localExpression);
          this.render();
          this.mpe.sendPitchBend(noteId, semitones);
          this.mpe.sendSlide(noteId, normalizedY);
          this.mpe.sendPressure(noteId, event.pressure);
        }
      }
    }
  }

  private handlePointerUp(event: PointerEvent): void {
    const currentButton = this.pointerDown.get(event.pointerId);
    if (currentButton) this.stopPointerNote(event.pointerId, currentButton.coordX, currentButton.coordY);
    this.pointerDown.delete(event.pointerId);
    if (this.pointerWiggle.has(event.pointerId)) {
      this.pointerWiggle.delete(event.pointerId);
      if (this.pointerWiggle.size === 0 && this.synth.getVibrato() && !this.arrowLeftHeld && !this.arrowRightHeld) {
        this.synth.setVibrato(false);
      }
    }
    try { this.canvas.releasePointerCapture(event.pointerId); } catch { /* iOS Safari */ }
  }

  private getButtonAtPointer(event: PointerEvent): { coordX: number; coordY: number } | null {
    if (!this.visualizer) return null;
    const rect = this.getCanvasRect();
    return this.visualizer.getButtonAtPoint(event.clientX - rect.left, event.clientY - rect.top);
  }
  private playPointerNote(pointerId: number, coordX: number, coordY: number, pressure = 0.7): void {
    const effectiveCoordX = coordX + this.transposeOffset;
    const effectiveCoordY = coordY + this.octaveOffset;
    const audioNoteId = `ptr_${pointerId}_${effectiveCoordX}_${effectiveCoordY}`;
    if (this.calibrating) {
      this.calibratedCells.add(`${effectiveCoordX}_${effectiveCoordY}`);
      this.visualizer?.setCalibratedRange(new Set(this.calibratedCells));
    }
    this.synth.playNote(audioNoteId, effectiveCoordX, coordY, this.octaveOffset);
    const midiNote = coordToMidiNote(effectiveCoordX, effectiveCoordY);
    this.mpe.noteOn(audioNoteId, midiNote, Math.max(0.01, pressure));
    this.activeNotes.set(`ptr_${pointerId}`, { coordX, coordY });
    this.trackNoteOn(effectiveCoordX, effectiveCoordY, midiNote);
    if (this.gameActor?.getSnapshot().matches('playing')) {
      this.gameActor.send({ type: 'NOTE_PRESSED', cellId: `${effectiveCoordX}_${effectiveCoordY}`, midiNote });
    }
    this.render();
  }

  private stopPointerNote(pointerId: number, coordX: number, coordY: number): void {
    const effectiveCoordX = coordX + this.transposeOffset;
    const effectiveCoordY = coordY + this.octaveOffset;
    const audioNoteId = `ptr_${pointerId}_${effectiveCoordX}_${effectiveCoordY}`;
    const midiNote = coordToMidiNote(effectiveCoordX, effectiveCoordY);
    this.synth.stopNote(audioNoteId);
    this.mpe.noteOff(audioNoteId, midiNote);
    this.localExpression.delete(audioNoteId);
    this.visualizer?.setMPEExpression(this.localExpression);
    this.activeNotes.delete(`ptr_${pointerId}`);
    this.trackNoteOff(effectiveCoordX, effectiveCoordY);
    this.render();
  }
  private trackNoteOn(coordX: number, coordY: number, midiNote: number): void {
    const key = `${coordX},${coordY}`;
    const count = this.noteHoldCounts.get(key) ?? 0;
    if (count === 0) {
      this.historyVisualizer?.noteOn(coordX, coordY, midiNote);
    }
    this.noteHoldCounts.set(key, count + 1);
    this.resetIdleTimer();
  }

  private trackNoteOff(coordX: number, coordY: number): void {
    const key = `${coordX},${coordY}`;
    const count = this.noteHoldCounts.get(key) ?? 0;
    if (count <= 1) {
      this.noteHoldCounts.delete(key);
      this.historyVisualizer?.noteOff(coordX, coordY);
    } else {
      this.noteHoldCounts.set(key, count - 1);
    }
  }

  public stopAllNotes(): void {
    this.sustainActor?.send({ type: 'DEACTIVATE' });
    this.vibratoActor?.send({ type: 'DEACTIVATE' });
    this.stopMpeVibrato();
    this.synth.stopAll();
    this.mpe.panic();
    this.historyVisualizer?.clearAll();
    this.activeNotes.clear();
    this.noteHoldCounts.clear();
    this.keyRepeat.clear();
    this.pointerDown.clear();
    this.midiChannelVoice.clear();
    this.render();
  }
  private recalcDefaultZoom(): void {
    if (!this.visualizer || this.pianoKeyPx <= 0) return;
    const geometry = this.visualizer.getGridGeometry();
    const hv1 = geometry.cellHv1, hv2 = geometry.cellHv2;
    const side1 = 2 * Math.sqrt(hv1.x * hv1.x + hv1.y * hv1.y);
    const side2 = 2 * Math.sqrt(hv2.x * hv2.x + hv2.y * hv2.y);
    const gridCellSidePx = Math.min(side1, side2);
    this.defaultZoom = this.pianoKeyPx / gridCellSidePx;
  }

  private resetIdleTimer(): void {
    const gameState = this.gameActor ? String(this.gameActor.getSnapshot().value) : undefined;
    if (gameState === 'playing') return;

    if (this.idleTimeout !== null) {
      clearTimeout(this.idleTimeout);
      this.idleTimeout = null;
    }

    if (this.isIdle) {
      this.setIdleState(false);
    }

    this.idleTimeout = setTimeout(() => {
      this.idleTimeout = null;
      const gs = this.gameActor ? String(this.gameActor.getSnapshot().value) : undefined;
      if (gs !== 'playing') {
        this.setIdleState(true);
      }
    }, 3000);
  }

  private setIdleState(idle: boolean): void {
    this.isIdle = idle;

    const graffitiEl = document.querySelector<SVGElement>('.graffiti-overlay');
    if (graffitiEl) { graffitiEl.classList.toggle('visible-full', idle); graffitiEl.classList.toggle('faded', !idle); }

    const songBarHint = document.getElementById('song-bar-hint');
    if (songBarHint) { songBarHint.classList.toggle('visible-full', idle); songBarHint.classList.toggle('faded', !idle); }

    this.historyVisualizer?.setIdleState(idle);
  }
  private async handleSearchResultClick(result: MidiSearchResult): Promise<void> {
    const resultsDiv = document.getElementById('midi-search-results');
    if (resultsDiv) resultsDiv.textContent = 'Loading\u2026';
    try {
      const response = await fetch(result.fetchUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const buffer = await response.arrayBuffer();
      this.loadMidiFromBuffer(buffer, result.title);
      if (resultsDiv) resultsDiv.textContent = '';
    } catch (err) {
      if (resultsDiv) resultsDiv.textContent = 'Load failed';
      const actor = this.gameActor;
      if (actor) {
        const msg = err instanceof Error ? err.message : 'Failed to fetch MIDI';
        actor.send({ type: 'LOAD_FAILED', error: msg });
      }
    }
  }
  private loadMidiFromBuffer(buffer: ArrayBuffer, songTitle: string): void {
    const actor = this.gameActor;
    if (!actor) return;

    const titleEl = document.querySelector<HTMLElement>('#game-song-title');
    if (titleEl) {
      titleEl.textContent = songTitle;
    }

    const badgeEl = document.querySelector<HTMLElement>('#game-quantization-badge');
    const levelBtn = getElementOrNull('quantization-level', HTMLButtonElement);
    const rawLevel = levelBtn?.value ?? 'none';
    const level: QuantizationLevel = rawLevel === '1/4' || rawLevel === '1/8' || rawLevel === '1/16' ? rawLevel : 'none';
    if (badgeEl) {
      badgeEl.textContent = level === 'none' ? '' : `Q:${level}`;
    }

    actor.send({ type: 'FILE_DROPPED', file: new File([], `${songTitle}.mid`) });

    try {
      const { events, tempoMap, timeSigMap } = parseMidi(buffer);
      const quantizedEvents = quantizeNotes(events, tempoMap, timeSigMap, level);

      const medianMidi = computeMedianMidiNote(quantizedEvents);

      let groups = buildNoteGroups(quantizedEvents, this.maxSimultaneousKeys);

      const range = this.calibratedRange;
      let semitones = 0;
      if (range && range.size > 0) {
        semitones = findOptimalTransposition(groups, range);
        groups = transposeSong(groups, semitones);
        groups = foldOctaves(groups, range);
      }

      const adjustedMedianMidi = medianMidi + semitones;
      const adjustedMedianHz = 440 * Math.pow(2, (adjustedMedianMidi - 69) / 12);
      const dRefSlider = getElementOrNull('d-ref-slider', HTMLInputElement);
      if (dRefSlider) {
        const dMin = parseFloat(dRefSlider.min);
        const dMax = parseFloat(dRefSlider.max);
        dRefSlider.value = Math.max(dMin, Math.min(dMax, adjustedMedianHz)).toFixed(2);
        dRefSlider.dispatchEvent(new Event('input', { bubbles: true }));
      }

      if (groups.length === 0) {
        actor.send({ type: 'LOAD_FAILED', error: 'No playable notes found in MIDI file' });
        return;
      }

      const tuningSliderGame = getElementOrNull('tuning-slider', HTMLInputElement);
      const currentTuning = parseFloat(tuningSliderGame?.value ?? '700');
      const needsTuningWarning = Math.abs(currentTuning - 700) > 0.5;
      if (tuningSliderGame) {
        tuningSliderGame.value = '700';
        tuningSliderGame.dispatchEvent(new Event('input', { bubbles: true }));
      }
      actor.send({ type: 'SONG_LOADED', noteGroups: groups });
      if (needsTuningWarning) {
        this.showTuningWarning();
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to parse MIDI file';
      actor.send({ type: 'LOAD_FAILED', error: msg });
    }
  }
  private showGameScore(elapsedSec: string): void {
    const existing = document.getElementById('game-score-overlay');
    if (existing) existing.remove();

    const overlay = document.createElement('div');
    overlay.id = 'game-score-overlay';

    const heading = document.createElement('div');
    heading.className = 'score-heading';
    heading.textContent = 'Complete!';

    const time = document.createElement('div');
    time.className = 'score-time';
    time.textContent = `${elapsedSec}s`;

    const btn = document.createElement('button');
    btn.className = 'score-btn';
    btn.textContent = 'Play again';
    btn.addEventListener('click', () => {
      overlay.remove();
      this.gameActor?.send({ type: 'GAME_RESET' });
    });

    overlay.appendChild(heading);
    overlay.appendChild(time);
    overlay.appendChild(btn);
    document.body.appendChild(overlay);
  }

  private showTuningWarning(): void {
    const existing = document.getElementById('game-tuning-warning');
    if (existing) existing.remove();

    const banner = document.createElement('div');
    banner.id = 'game-tuning-warning';
    banner.textContent = 'Tuning set to 12-TET for game mode';

     const dismiss = (): void => { banner.remove(); };
     banner.addEventListener('click', dismiss);
     setTimeout(dismiss, 3000);

     document.body.appendChild(banner);
   }

  private getMpeNoteIds(): string[] {
    const ids: string[] = [];
    for (const [key, { coordX, coordY }] of this.activeNotes) {
      if (key.startsWith('ptr_')) {
        ids.push(`${key}_${coordX + this.transposeOffset}_${coordY + this.octaveOffset}`);
      } else if (key.startsWith('midi_')) {
        ids.push(`${key}_${coordX}_${coordY}`);
      } else {
        ids.push(`key_${key}_${coordX + this.transposeOffset}_${coordY + this.octaveOffset}`);
      }
    }
    return ids;
  }

  private startMpeVibrato(): void {
    if (!this.mpe.isEnabled() || this.vibratoRAF !== null) return;
    this.vibratoPhase = 0;
    const tick = (): void => {
      this.vibratoPhase += 0.314; // ≈5Hz at 60fps
      const bend = Math.sin(this.vibratoPhase) * 0.5; // ±0.5 semitones
      for (const noteId of this.getMpeNoteIds()) {
        this.mpe.sendPitchBend(noteId, bend);
      }
      this.vibratoRAF = requestAnimationFrame(tick);
    };
    this.vibratoRAF = requestAnimationFrame(tick);
  }

  private stopMpeVibrato(): void {
    if (this.vibratoRAF !== null) {
      cancelAnimationFrame(this.vibratoRAF);
      this.vibratoRAF = null;
    }
    for (const noteId of this.getMpeNoteIds()) {
      this.mpe.sendPitchBend(noteId, 0);
    }
    this.vibratoPhase = 0;
  }

  private startArrowVibrato(): void {
    if (this.arrowVibratoInterval !== null) return;
    this.arrowVibratoPhase = 0;
    this.arrowVibratoInterval = setInterval(() => {
      this.arrowVibratoPhase += 0.52; // ~5Hz at 60 ticks/s
      const bend = Math.sin(this.arrowVibratoPhase);
      for (const [noteCode, noteData] of this.activeNotes) {
        const cx = noteData.coordX + this.transposeOffset;
        const cy = noteData.coordY + this.octaveOffset;
        const noteId = `key_${noteCode}_${cx}_${cy}`;
        this.synth.setPitchBend(noteId, bend);
      }
    }, 16);
  }

  private stopArrowVibrato(): void {
    if (this.arrowVibratoInterval !== null) {
      clearInterval(this.arrowVibratoInterval);
      this.arrowVibratoInterval = null;
    }
    this.arrowVibratoPhase = 0;
  }
  private render(): void {
    if (!this.visualizer) return;
    if (this.renderScheduled) return;
    this.renderScheduled = true;
    requestAnimationFrame(() => {
      this.renderScheduled = false;
      if (!this.visualizer) return;
      const fifth = this.synth.getFifth();
      const activePitchCents = new Set(
        Array.from(this.activeNotes.values()).map(({ coordX, coordY }) =>
          Math.round(coordX * fifth + coordY * 1200)
        )
      );
      const activeNoteIds = this.visualizer.getCellIdsForPitchCents(activePitchCents, fifth);
      const sourceIds = Array.from(this.activeNotes.values()).map(({ coordX, coordY }) => `${coordX}_${coordY}`);
      this.visualizer.setActiveNotes(activeNoteIds, sourceIds);
      this.visualizer.render();
    });
  }

  private getCanvasRect(): DOMRect {
    this.cachedCanvasRect = this.canvas.getBoundingClientRect();
    return this.cachedCanvasRect;
  }
  private setCalibrationDimming(dim: boolean): void {
    const method = dim ? 'add' : 'remove';
    document.getElementById('visualiser-panel')?.classList[method]('dimmed');
    document.getElementById('pedals-panel')?.classList[method]('dimmed');
    document.querySelector('.top-bar-left')?.classList[method]('dimmed');
    document.getElementById('song-bar-search')?.classList[method]('dimmed');
  }

  private enterCalibrationMode(): void {
    this.calibrating = true;
    this.calibratedCells = new Set();
    this.visualizer?.setCalibratedRange(new Set<string>());
    const banner = document.getElementById('calibration-banner');
    const warning = document.getElementById('calibration-warning');
    if (banner) banner.classList.add('show-flex');
    const gameState = this.gameActor ? String(this.gameActor.getSnapshot().value) : undefined;
    if (gameState === 'playing' || gameState === 'loading') {
      this.gameActor?.send({ type: 'GAME_RESET' });
    }
    if (warning && (gameState === 'playing' || gameState === 'loading' || gameState === 'complete')) {
      warning.classList.add('show-flex');
      warning.textContent = 'Song paused for calibration';
      setTimeout(() => { warning.classList.remove('show-flex'); }, 2000);
    } else if (warning) {
      warning.classList.remove('show-flex');
    }
    const btn = document.getElementById('calibrate-btn') as HTMLButtonElement | null;
    if (btn) {
      btn.classList.add('active');
      btn.disabled = true;
    }
    this.setCalibrationDimming(true);
    this.render();
  }

  private exitCalibrationMode(confirm: boolean): void {
    this.calibrating = false;
    if (confirm) {
      if (this.calibratedCells.size === 0) {
        clearCalibratedRange();
        this.calibratedRange = null;
      } else {
        saveCalibratedRange(this.calibratedCells);
        this.calibratedRange = new Set(this.calibratedCells);
      }
      const count = this.calibratedCells.size;
      const msg = document.getElementById('calibration-msg');
      if (msg) {
        msg.textContent = count > 0 ? `Range saved (${count} keys)` : 'Range cleared — all keys playable';
        setTimeout(() => { msg.textContent = 'Play all notes'; }, 2000);
      }
    }
    this.visualizer?.setCalibratedRange(this.calibratedRange);
    this.calibratedCells = new Set();
    const banner = document.getElementById('calibration-banner');
    if (banner) banner.classList.remove('show-flex');
    const btn = document.getElementById('calibrate-btn') as HTMLButtonElement | null;
    if (btn) {
      btn.classList.remove('active');
      btn.disabled = false;
    }
    this.setCalibrationDimming(false);
    if (confirm && this.lastMidiBuffer) {
      this.loadMidiFromBuffer(this.lastMidiBuffer, this.lastMidiTitle);
    }
    this.render();
  }
  private labelLayout: LabelLayout = 'qwerty';

  private buildKeyboardLabels(): Map<string, string> {
    const map = new Map<string, string>();
    for (const [code, coord] of Object.entries(this.currentLayout.keyMap)) {
      const label = codeToLabel(code, this.labelLayout);
      if (label) {
        map.set(`${coord[0]}_${coord[1]}`, label);
      }
    }
    return map;
  }
}
